

/**
 * @component LaydownLogComponent
 * ------------------------------
 * Manages the display and interaction with the laydown space logs, fetching logs based on the selected location,
 * configuring the table and UI text, and providing forms for filtering and editing log entries.
 *
 * This component ensures real-time updates to the log data and UI elements based on user input,
 * form submissions, and service responses.
 *
 * Key Features:
 * - Location-aware: Conditionally renders based on whether selected location supports laydown space.
 *   Shows LaydownLocationGuardComponent with helpful message when location doesn't support laydown.
 * - Dynamic table display with pagination, sorting, and filtering capabilities.
 * - Modal forms for filtering logs and editing individual log entries.
 * - Date picker integration for selecting dates in forms.
 *
 * Subscriptions:
 * - `isLargeScreenSubscription`: Tracks screen size changes to adjust UI layout dynamically.
 * - `logSubscription`: Listens for updates to the laydown logs in the log service and updates the table accordingly.
 * - `selectedLocationSubscription`: Responds to location changes, updates log data, page title, and triggers conditional rendering based on laydown availability.
 * - `logsLoadingSubscription`: Monitors the loading state of log data through the log service to provide user feedback.
 *
 * Notes:
 * - This component relies on multiple services (`LaydownLogService`, `AssetsDataService`, `ScreenSizeService`)
 *   to fetch and update log data dynamically.
 * - Subscriptions play a key role in managing real-time updates, ensuring the displayed logs and
 *   UI elements remain in sync with user interactions and backend responses.
 */

import { Component, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { IonInput } from '@ionic/angular';
import { LaydownLogService } from '../../../service/laydown-log.service';
import { ToastService } from '../../../service/toast.service';
import { ScreenSizeService } from '../../../service/screen-size.service';
import { SelectedLocationService } from '../../../service/selected-location.service';
import { ContactsService } from '../../../service/contact.service';
import { LaydownLogSearch } from '../../../models/logs/laydown-space/LaydownLogSearch';
import { LaydownLogRow } from '../../../models/logs/laydown-space/LaydownLogRow';
import { ApiLaydownLogRow } from '../../../models/logs/laydown-space/ApiLaydownLogRow';
import { GetLaydownLogsResponse } from '../../../models/logs/laydown-space/GetLaydownLogResponse';

import { Location } from '../../../models/assets-data/LocationConfig.interface';
import { AssetsDataService } from '../../../service/assets-data.service';
import { EntraService } from '../../../service/entra.service';

import { TableField } from '../../../models/assets-data/laydown/LaydownLogTableConfig.interface';
import { DataLoading } from '../../../models/DataLoading.interface';
import { formatDateString } from '../../../utils/date-functions';
import { Option } from '../../../models/assets-data/laydown/LaydownLogTableConfig.interface';
import {
  ToastText,
} from '../../../models/assets-data/UiConfig.interface';
import { SelectedLocation } from '../../../models/selectedLocation.interface';
import { User } from '../../../models/User.interface';
import { LaydownLogFilterParams } from '../../../models/logs/laydown-space/LaydownLogFilterParams';
import { ApiLaydownLogFilterParams } from '../../../models/logs/laydown-space/ApiLaydownFilterParams';
import { TableConfig } from '../../../models/table-config.interface';
import { isValidEmail } from '../../../shared/helpers/email-validator';

@Component({
  selector: 'app-laydown-log',
  templateUrl: './laydown-log-table.component.html',
  styleUrls: ['./laydown-log-table.component.css'],
})
export class LaydownLogTableComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('contactInput') contactInput!: IonInput;
  public contactInputElement?: HTMLElement;

  // Subscriptions for various observables
  public logsLoadingSubscription!: Subscription;
  public isLargeScreenSubscription!: Subscription;
  private logSubscription!: Subscription;
  private selectedLocationSubscription!: Subscription;
  private userSubscription!: Subscription;
  private jobNumberSubscription!: Subscription;
  private spaceSubscription!: Subscription;
  private employeeEmailsSubscription!: Subscription;
  private selectedLocationService: SelectedLocationService;

  // Services and utilities
  private _laydownLogService: LaydownLogService;
  private _entraService: EntraService;
  private _assetsDataService: AssetsDataService;
  private _toastService: ToastService;
  private _screenSizeService: ScreenSizeService;
  private _contactsService: ContactsService;
  private _router: Router;
  private _formBuilder: FormBuilder = new FormBuilder();

  // UI text and configuration data
  public toastText!: ToastText;

  // Screen size and loading state
  public isLargeScreen!: boolean;
  public logsLoading: DataLoading = { isLoading: false, message: '' };

  // User Data
  public user!: User;

  // Employee emails for autocomplete
  public employeeEmails: string[] = [];

  // Modal states
  public isFilterModalOpen = false;
  public isRowModalOpen = false;
  public isDateModalOpen = false;

  // Forms for filtering and editing logs
  public filterForm!: FormGroup;
  public editLogForm!: FormGroup;

  // Table configuration and state
  public userIsAdmin: boolean = false;
  public columnData!: TableField[];
  public filterLogsLabelPlacement: string = 'stacked';
  public editLogLabelPlacement: string = 'stacked';
  public tableConfig!: TableConfig;


  // Location and Page Title
  public selectedLocation: SelectedLocation = {
    name: 'norfolk',
    label: 'Norfolk',
    laydownSpaceAvailable: false,
  };

  public allLocations!: Location[];


  public pageTitle = this.selectedLocation.label + ' ' + 'Laydown Space Log';

  public currentPageTitle = this.pageTitle;

  // Date tracking for the date picker modal
  public tempSelectedDate: string | null = null;
  public selectedDate!: string;
  public selectedDateControl!: FormControl;

  // Log Filter/Search state
  public filterFormValues: LaydownLogFilterParams = new LaydownLogFilterParams();
  public filteredColumns: (keyof LaydownLogRow)[] = [];
  public filterColumnParams: { [key: string]: string | null } = {};

  public showLogsWithDateOut: boolean = false;
  public initialLogSearch!: LaydownLogSearch;
  public logSearch!: LaydownLogSearch;

  // Fetching Logs and Pagination
  public laydownLogResponse: GetLaydownLogsResponse =
    new GetLaydownLogsResponse();
  public logCount!: number;
  public rowsPerPage: number = 200;
  public totalPageCount!: number;
  public laydownLogs!: LaydownLogRow[];

  // Selected Row and Table Row Data
  public originalLaydownLogRow!: LaydownLogRow;
  public selectedRow!: LaydownLogRow;

  // Constructor with injected services and utilities
  constructor(
    laydownLogService: LaydownLogService,
    entraService: EntraService,
    formBuilder: FormBuilder,
    assetsDataService: AssetsDataService,
    screenSizeService: ScreenSizeService,
    toastService: ToastService,
    router: Router,
    selectedLocationService: SelectedLocationService,
    contactsService: ContactsService
  ) {
    this._laydownLogService = laydownLogService;
    this._entraService = entraService;
    this._toastService = toastService;
    this._formBuilder = formBuilder;
    this._router = router;
    this._screenSizeService = screenSizeService;
    this.isLargeScreen = this._screenSizeService.isLargeScreen.value;
    this._assetsDataService = assetsDataService;
    this._entraService = entraService;
    this.selectedLocationService = selectedLocationService;
    this._contactsService = contactsService;

    this.getUiConfig();
    this.initializeTableData();

    // Initialize filter params and log search after services are assigned
    this.filterColumnParams = this._laydownLogService.initialFilterColumnParams;
    this.initialLogSearch = new LaydownLogSearch({
      storageFacility: this.selectedLocation.name,
      filterColumnParams: { ...this._laydownLogService.initialFilterColumnParams },
    });
    this.logSearch = this.initialLogSearch;
  }

  // Manage Subscriptions and Fetch Logs on Init
  ngOnInit() {
    // Unsubscribe from previous subscriptions if they exist
    if (this.isLargeScreenSubscription) {
      this.isLargeScreenSubscription.unsubscribe();
    }
    if (this.logSubscription) {
      this.logSubscription.unsubscribe();
    }
    if (this.selectedLocationSubscription) {
      this.selectedLocationSubscription.unsubscribe();
    }
    if (this.logsLoadingSubscription) {
      this.logsLoadingSubscription.unsubscribe();
    }

    // Subscribe to screen size changes
    this.isLargeScreenSubscription =
      this._screenSizeService.isLargeScreen.subscribe(
        (isLargeScreen: boolean) => {
          this.isLargeScreen = isLargeScreen;
        }
      );

    // Subscribe to data loading state changes
    this.logsLoadingSubscription =
      this._laydownLogService.dataLoading.subscribe(
        (dataLoading: DataLoading) => {
          this.logsLoading = dataLoading;
        }
      );

    // Subscribe to fetched log updates and update log data and pagination
    this.logSubscription = this._laydownLogService.fetchedLaydownLogs.subscribe(
      (logs: GetLaydownLogsResponse) => {
        this.logCount = logs.totalRowCount;
        this.totalPageCount = Math.ceil(this.logCount / this.rowsPerPage);
        this.laydownLogs = logs.logList.map(
          (log: any) => new LaydownLogRow(log)
        );
      }
    );

    // Subscribe to location updates, fetch logs accordingly, and update page title
    this.selectedLocationSubscription =
      this.selectedLocationService.selectedLocation$.subscribe(
        (location: SelectedLocation) => {
          this.selectedLocation = location;
          this.initialLogSearch.storageFacility = location.label;
          const pageTitle = location.label + ' ' + 'Laydown Space Log';
          this.pageTitle = pageTitle;

          this.currentPageTitle = pageTitle;
          this.tableConfig.title = pageTitle;

          const logSearch = this.logSearch;
          logSearch.storageFacility = location.label;
          this._laydownLogService.fetchLaydownLogs(logSearch);
        }
      );

    this.userSubscription = this._entraService.user.subscribe((user: User) => {
      this.user = user;
    });

    // Subscribe to employee emails for autocomplete
    this.employeeEmailsSubscription = this._contactsService.fetchedCompanyEmails.subscribe(
      (emails: string[]) => {
        this.employeeEmails = emails;
      }
    );
  }

  /**
   * Cleanup subscriptions when the component is destroyed
   */
  ngOnDestroy() {
    this.logSubscription.unsubscribe();
    this.selectedLocationSubscription.unsubscribe();
    this.logsLoadingSubscription.unsubscribe();
    this.isLargeScreenSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
    
    if (this.jobNumberSubscription) {
      this.jobNumberSubscription.unsubscribe();
    }
    if (this.spaceSubscription) {
      this.spaceSubscription.unsubscribe();
    }
    if (this.employeeEmailsSubscription) {
      this.employeeEmailsSubscription.unsubscribe();
    }
  }

  /**
   * Initializes table data with assets configuration and sets up filtering form
   */
  public initializeTableData() {
    this.userIsAdmin = this._entraService.user.value.isAdmin;
    const tableData = this._assetsDataService.GetLaydownLogTableData();
    const columnData = tableData.inputs;
    //set location options for storage facility field

  const storageFacilityField = columnData.find(field => field.name === 'storageFacility');
  if (storageFacilityField) {
    const allLocations = this._assetsDataService.GetLocationData();
    const locationOptions: Option[] = allLocations.map(location => ({
      label: location.locationLabel,
      value: location.locationLabel
    }));
    storageFacilityField.options = locationOptions;
  }

    this.columnData = columnData;
    const filterForm = this.createFilterFormGroup(
      columnData,
      this._formBuilder
    );
    this.filterForm = filterForm;
  }

  /**
   * Fetches and sets UI configuration data from the assets data service.
   */
  public getUiConfig(): void {
    const uiConfigData = this._assetsDataService.GetUiConfigData();
    const labelPlacement = uiConfigData.labelPlacement;
    this.filterLogsLabelPlacement = labelPlacement.filterLogs;
    this.editLogLabelPlacement = labelPlacement.editLog;
    this.toastText = uiConfigData.toastText;

    const pageTitle = this.selectedLocation.label + ' ' + 'Laydown Space Log';
    this.pageTitle = pageTitle;


    this.tableConfig = {
      title: pageTitle,
      notes: {
        noLogs: 'No laydown logs found for the selected filters.',
      },
      buttons: {
        filterButton: 'Filter',
        newEntryButton: 'New Entry',
        deleteEntryButton: 'Delete Entry',
        viewLogsButton: 'View Laydown Logs',
      },
      hasEmailFeature: false,
    };
  }


  /**
   * Navigates to the "New Log" page
   */
  public onNavToNewLogPage() {
    this._router.navigate(['/laydown/new-log']);
  }

  // Table Action Button Methods ---------------------------------------------------------------------

  /**
   * Deletes the selected log entry.
   */
  public onDeleteLogRow(logId: number): void {
    const confirmDelete = confirm(
      'Are you sure you want to delete this log entry?'
    );
    if (!confirmDelete) return;

    const onSuccessFunction = () => {
      this._laydownLogService.fetchLaydownLogs(this.logSearch);
    };

    this._laydownLogService.deleteLog(logId, onSuccessFunction);
  }

  // DatePicker Modal Methods ---------------------------------------------------------------------

  /**
   * Opens the date picker modal for a form field
   */
  onOpenDateModal(formFieldName: string, formGroup: FormGroup) {
    const dateControl = formGroup.get(formFieldName);

    if (dateControl) {
      this.selectedDateControl = dateControl as FormControl;

      if (dateControl.value === null) {
        this.tempSelectedDate = new Date().toISOString();
      } else {
        this.tempSelectedDate = new Date(dateControl.value).toISOString();
      }

      this.isDateModalOpen = true;
    }
  }

  /**
   * Updates the selected date from the date picker modal
   */
  public updateSelectedDate(date: string) {
    if (date !== null) this.tempSelectedDate = new Date(date).toISOString();
  }

  /**
   * Closes the date picker modal and saves the selected date
   */
  closeDateModal() {
    if (this.tempSelectedDate !== null) {
      const selectedDate = formatDateString(this.tempSelectedDate);
      this.selectedDateControl.setValue(selectedDate);
    }
    this.isDateModalOpen = false;
  }

  /**
   * Cancels date selection and closes the modal
   */
  cancelDateSelection() {
    this.tempSelectedDate = null;
    this.isDateModalOpen = false;
  }

  // View / Update / Delete Log Methods ---------------------------------------------------------------------

  /**
   * Creates a FormGroup for editing a log entry.
   */
  public createEditLogFormGroup(
    data: any[],
    row: LaydownLogRow,
    fb: FormBuilder
  ): FormGroup {
    const formControls: { [key: string]: FormControl } = {};

    data.forEach((field) => {
      let startingValue = row[field.name as keyof LaydownLogRow];
      const validators = field.required ? [Validators.required] : [];

      // Add email validator if formFieldType is email
      if (field.formFieldType === 'email') {
        validators.push(Validators.email);
      }

      const controlOptions = {
        value: startingValue,
        disabled: field.disabled && !this.userIsAdmin ? true : false,
      };
      formControls[field.name] = fb.control(controlOptions, validators);
    });

    return fb.group(formControls);
  }

  /**
   * Opens the row details modal when a user clicks a row.
   */
  onOpenRowModal(item: LaydownLogRow) {
    this.selectedRow = item;
    this.isRowModalOpen = true;
    const filterFormBuilder = new FormBuilder();
    this.editLogForm = this.createEditLogFormGroup(
      this.columnData,
      item,
      filterFormBuilder
    );

    this.updateFormControlStates();
    
    // Get native element from IonInput after view is initialized
    setTimeout(async () => {
      if (this.contactInput) {
        this.contactInputElement = await this.contactInput.getInputElement();
      }
    });
    
    // Unsubscribe from previous subscriptions if they exist
    if (this.jobNumberSubscription) {
      this.jobNumberSubscription.unsubscribe();
    }
    if (this.spaceSubscription) {
      this.spaceSubscription.unsubscribe();
    }
    
    // Subscribe to job number field changes to validate space occupancy
    this.jobNumberSubscription = this.editLogForm.get('jobNumber')!.valueChanges.subscribe(
      () => {
        this.validateEditFormSpaceOccupancy();
      }
    );

    // Subscribe to space field changes to validate space occupancy
    this.spaceSubscription = this.editLogForm.get('space')!.valueChanges.subscribe(
      () => {
        this.validateEditFormSpaceOccupancy();
      }
    );
  }

  /**
   * Lifecycle hook called after view is initialized
   */
  ngAfterViewInit() {
    // ViewChild will be available here if needed
  }

  /**
   * Closes the view-row-details modal
   */
  closeRowModal() {
    this.isRowModalOpen = false;
  }

  /**
   * Retrieves the values from the edit log form
   */
  public getEditLogFormValues(): LaydownLogRow {
    const formControls = this.editLogForm.controls;
    const editLogData: { [key: string]: any } = {};

    Object.keys(formControls).forEach((key) => {
      editLogData[key as keyof LaydownLogRow] = formControls[key].value;
    });

    const laydownLogData = new LaydownLogRow(editLogData);
    return laydownLogData;
  }

  /**
   * Validates that the job number and space combination is not already occupied in the edit form.
   * Excludes the original values of the selected row from validation to allow no-change saves.
   */
  private validateEditFormSpaceOccupancy(): void {
    const jobNumber = this.editLogForm.get('jobNumber')?.value;
    const space = this.editLogForm.get('space')?.value;

    // Only validate if both fields have values
    if (!jobNumber || !space) {
      return;
    }

    // Skip validation if values haven't changed from original
    if (jobNumber === this.selectedRow.jobNumber && space === this.selectedRow.space) {
      const jobNumberControl = this.editLogForm.get('jobNumber');
      if (jobNumberControl?.hasError('occupied')) {
        jobNumberControl.setErrors(null);
      }
      return;
    }

    // Check with service if this space is occupied by this job number
    if (this._laydownLogService.isSpaceOccupiedByJob(jobNumber, space)) {
      this._toastService.showWarningMessage(
        `Job ${jobNumber} is already occupying Space ${space}. Please select a different space or job number.`
      );
      
      // Mark job number field as invalid
      this.editLogForm.get('jobNumber')?.setErrors({ occupied: true });
    } else {
      // Clear the occupied error if it was previously set
      const jobNumberControl = this.editLogForm.get('jobNumber');
      if (jobNumberControl?.hasError('occupied')) {
        jobNumberControl.setErrors(null);
      }
    }
  }

  /**
   * Updates the log row if there are changes in the form.
   */
  public updateLogRow() {
    if (this.editLogForm.valid) {
      const editLogData = this.getEditLogFormValues();
      const updateLogRowValues: any = {};

      for (const column of this.columnData) {
        if (editLogData.hasOwnProperty(column.name)) {
          if (
            editLogData[column.name as keyof LaydownLogRow] !==
            this.selectedRow[column.name as keyof LaydownLogRow]
          ) {
            updateLogRowValues[column.name] =
              editLogData[column.name as keyof LaydownLogRow];
          }
        }
      }

      if (Object.keys(updateLogRowValues).length > 0) {
        updateLogRowValues.id = this.selectedRow.id;
        const onSuccessFunction = () => {
          // Update occupied spaces if job number or space changed
          const originalJobNumber = this.selectedRow.jobNumber;
          const originalSpace = this.selectedRow.space;
          const newJobNumber = editLogData.jobNumber;
          const newSpace = editLogData.space;
          
          if (originalJobNumber && originalSpace && newJobNumber && newSpace) {
            if (originalJobNumber !== newJobNumber || originalSpace !== newSpace) {
              this._laydownLogService.updateOccupiedSpacesAfterEdit(
                originalJobNumber,
                originalSpace,
                newJobNumber,
                newSpace
              );
            }
          }
          
          this.editLogForm.reset();
          this.isRowModalOpen = false;
          this._laydownLogService.fetchLaydownLogs(this.logSearch);
        };

        this._laydownLogService.updateLog(
          updateLogRowValues,
          onSuccessFunction
        );
      } else {
        this._toastService.showWarningMessage(
          this.toastText.logEntry.update.noChanges
        );
      }
    } else {
      this.editLogForm.markAllAsTouched();
      this._toastService.showWarningMessage(
        this.toastText.logEntry.update.invalidFormError
      );
    }
  }

  /**
   * Opens a new window displaying row details
   */
  public openRowDetailsWindow() {
    const escapeHTML = (str: string) => {
      return str.replace(
        /[&<>"']/g,
        (match) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
          }[match] || match)
      );
    };

    let tableRows = this.columnData
      .map(
        (column: TableField) => `
        <tr>
          <td><strong>${escapeHTML(column.label)}:</strong></td>
          <td>${escapeHTML(
            this.selectedRow[
              column.name as keyof LaydownLogRow
            ]?.toString() || 'N/A'
          )}</td>
        </tr>
      `
      )
      .join('');

    const innerHTML = `
        <html>
          <head>
            <title>Log Details</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h2 { color: #333; }
              table { width: 100%; border-collapse: collapse; }
              td { padding: 8px; border-bottom: 1px solid #ddd; }
            </style>
          </head>
          <body>
            <h2>${this.selectedLocation.label} Laydown Space Log Entry Details</h2>
            <table>${tableRows}</table>
          </body>
        </html>
      `;

    const newWindow = window.open('', '_blank', 'width=600,height=400');
    if (!newWindow) {
      console.error('Failed to open the new window.');
      this._toastService.showWarningMessage(this.toastText.window.popoutError);
      return;
    }

    if (newWindow) {
      newWindow.document.open();
      newWindow.document.write(innerHTML);
      newWindow.document.close();
    }
  }

  // Filtering/Sorting Methods ---------------------------------------------------------------------

  /**
   * Resets all filters to their initial state.
   */
  public onClearFilters() {
    this.filterForm.reset();
    this.showLogsWithDateOut = false;
    this.logSearch = this.initialLogSearch;
    this.updateFilters(this._laydownLogService.initialFilterColumnParams);
  }

  /**
   * Creates a FormGroup for filtering logs
   */
  public createFilterFormGroup(data: any[], fb: FormBuilder): FormGroup {
    const formControls: { [key: string]: FormControl } = {};

    data.forEach((field) => {
      const startingValue =
        this.filterFormValues[field.name as keyof LaydownLogFilterParams];
      formControls[field.name] = fb.control(startingValue);
    });
    formControls['_ALL'] = fb.control(this.filterFormValues['_ALL']);
    return fb.group(formControls);
  }

  /** Opens the filter modal */
  public onOpenFilterModal() {
    this.isFilterModalOpen = true;
  }

  /** Closes the filter modal */
  public closeFilterModal() {
    this.isFilterModalOpen = false;
  }

 

  /**
   * Updates the enabled/disabled state of form controls
   */
  private updateFormControlStates() {
    if (!this.editLogForm) return;

    this.columnData.forEach((column) => {
      const control = this.editLogForm.get(column.name);
      if (control) {
        if (this.shouldDisableField(column)) {
          control.disable();
        } else {
          control.enable();
        }
      }
    });
  }

  /** Toggles the display of logs with 'dateOut' value */
  public onToggleLogsWithDateOut() {
    const updatedShowLogsWithDateOut = !this.showLogsWithDateOut;
    if (!updatedShowLogsWithDateOut) {
   
      this.filterForm.controls['dateOut'].setValue(null);
    }
    this.showLogsWithDateOut = !this.showLogsWithDateOut;
  }

  /**
   * Extracts and transforms filter values from the form for API submission
   */
  public getFilterValuesFromForm(): ApiLaydownLogFilterParams {
    const generalSearch = this.filterForm.controls['_ALL'].value;
    const standardFieldValues: { [key: string]: any } = {};

    Object.keys(this.filterForm.controls)
      .filter((key) => key !== '_ALL')
      .forEach((key) => {
        standardFieldValues[key] = this.filterForm.controls[key].value;

        if (key === 'dateOut' && this.filterForm.controls[key].value) {
          this.showLogsWithDateOut = true;
        }
      });

    const laydownRowData = new LaydownLogRow(standardFieldValues);
    const apiLaydownData = new ApiLaydownLogRow(laydownRowData);
    const apiLogFilterParams = new ApiLaydownLogFilterParams(apiLaydownData);
    this.filterFormValues = new LaydownLogFilterParams(laydownRowData);

    if (generalSearch) {
      apiLogFilterParams['_ALL'] = generalSearch;
      this.filterFormValues['_ALL'] = generalSearch;
    }

    return apiLogFilterParams;
  }

  /**
   * Applies user-defined filters to the laydown log search
   */
  public onSubmitFilters() {
    this.closeFilterModal();
  

    this.logSearch.filterColumnParams = this.logSearch.filterColumnParams || {};
    if (this.showLogsWithDateOut) {
      delete this.logSearch.filterColumnParams['dateOut'];
    } else {
      this.logSearch.filterColumnParams['dateOut'] = null;
    }

    const filterFormData = this.getFilterValuesFromForm();
    const filterColumnParams: Partial<
      Record<keyof LaydownLogFilterParams, string | Date | number | boolean>
    > = {};

    Object.keys(filterFormData).forEach((key) => {
      const filterValue = filterFormData[key as keyof typeof filterFormData];

      if (
        filterValue !== undefined &&
        filterValue !== null &&
        filterValue !== ''
      ) {
        const columnName = key;
        filterColumnParams[columnName as keyof LaydownLogFilterParams] = filterValue;
      }
    });

    this.updateFilters(filterColumnParams);
  }

  /**
   * Updates the filters applied to the log search
   */
  public updateFilters(
    filterColumnParams: Partial<
      Record<keyof ApiLaydownLogRow, string | Date | Number | Boolean>
    >
  ) {
    const searchUpdate: LaydownLogSearch = { ...this.logSearch };

    const UpdatedFilterColumnParams = {
      ...this.logSearch.filterColumnParams,
      ...filterColumnParams,
    };

    searchUpdate.filterColumnParams = UpdatedFilterColumnParams;
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Sorts the log search results by the specified column
   */
  public onSortByColumn(columnName: string) {
    const searchUpdate: LaydownLogSearch = { ...this.logSearch };
    searchUpdate.sortColumn = columnName;
    searchUpdate.sortOrderDesc = !searchUpdate.sortOrderDesc;
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Sorts by a column in an explicit direction (from the mobile sort sheet),
   * rather than toggling the current order the way a header click does.
   */
  public onSortByColumnDirection(event: { column: string; desc: boolean }) {
    const searchUpdate: LaydownLogSearch = { ...this.logSearch };
    searchUpdate.sortColumn = event.column;
    searchUpdate.sortOrderDesc = event.desc;
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Updates the page number for pagination
   */
  public onUpdatePageNumber(pageNumber: number) {
    const rowStart = pageNumber * this.rowsPerPage;
    const rowEnd = rowStart + this.rowsPerPage - 1;
    const searchUpdate: LaydownLogSearch = { ...this.logSearch };
    searchUpdate.rowStart = rowStart;
    searchUpdate.rowEnd = rowEnd;
    this.logSearch = searchUpdate;
    this.updateLogSearch(searchUpdate);
  }

  /**
   * Updates the log search parameters and triggers a fetch operation
   */
  public updateLogSearch(searchUpdate: LaydownLogSearch) {
    const newLogFetchParams = { ...this.logSearch, ...searchUpdate };
    this.logSearch = newLogFetchParams;
    this._laydownLogService.fetchLaydownLogs(newLogFetchParams);
  }

  /**
   * Determines if the clear field button should be shown
   */
  public shouldShowClearField(column: any): boolean {
    return (column.disabled && !this.userIsAdmin);
  }

  /**
   * Determines if the edit field should be disabled
   */
  public shouldDisableField(column: any): boolean {
    return column.disabled && !this.userIsAdmin;
  }

  /**
   * Returns the option list for a field based on its optionListId
   */
  public getOptionsForField(field: TableField): string[] {
    if (!field.optionListId) {
      return [];
    }
    
    if (field.optionListId === 'employeeEmails') {
      return this.employeeEmails;
    }
    
    return [];
  }

  /**
   * Get error text for a form control
   */
  public getErrorText(controlName: string, label: string): string {
    const control = this.editLogForm?.get(controlName);
    
    if (!control || !control.touched) {
      return '';
    }

    // Log all errors for debugging
    if (control.errors) {
      console.log(`Errors for ${controlName}:`, control.errors);
    }

    // Check for specific errors
    if (control.hasError('required')) {
      return `${label} is required`;
    }

    if (control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('occupied')) {
      return 'This space is already occupied by this job';
    }

    return '';
  }
}